import React, { useState, useEffect } from "react";
import { Plus, Trash2, Phone, User, Mail } from "lucide-react";
import supabase from "../supabaseClient";

const ExternalContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("external_contacts")
        .select("*")
        .order("name");
      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error("Error loading contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizePhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return raw.trim(); // already formatted or international — leave as-is
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const normalizedPhone = phone.trim() ? normalizePhone(phone) : null;
    const normalizedEmail = email.trim() || null;
    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("external_contacts")
          .update({ name: name.trim(), phone: normalizedPhone, email: normalizedEmail })
          .eq("id", editingId);
        if (error) throw error;
        setContacts((prev) =>
          prev.map((c) =>
            c.id === editingId
              ? { ...c, name: name.trim(), phone: normalizedPhone, email: normalizedEmail }
              : c
          )
        );
      } else {
        const { data, error } = await supabase
          .from("external_contacts")
          .insert([{ name: name.trim(), phone: normalizedPhone, email: normalizedEmail }])
          .select()
          .single();
        if (error) throw error;
        setContacts((prev) => [...prev, data]);
      }
      setName("");
      setPhone("");
      setEmail("");
      setEditingId(null);
    } catch (error) {
      console.error("Error saving contact:", error);
      alert("Error saving contact.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setName(contact.name);
    setPhone(contact.phone || "");
    setEmail(contact.email || "");
  };

  const handleCancel = () => {
    setEditingId(null);
    setName("");
    setPhone("");
    setEmail("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this contact?")) return;
    setContacts((prev) => prev.filter((c) => c.id !== id));
    try {
      const { error } = await supabase.from("external_contacts").delete().eq("id", id);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting contact:", error);
      loadContacts();
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        Add grandparents, coaches, or anyone who should receive updates. Email
        contacts get schedule emails with a calendar subscription link. Phone
        contacts get SMS alerts. They don't need an account.
      </p>

      {/* Add / Edit form */}
      <form
        onSubmit={handleSave}
        className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2"
      >
        <h4 className="text-sm font-medium text-gray-700">
          {editingId ? "Edit Contact" : "Add Contact"}
        </h4>
        <div className="relative">
          <User size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="w-full border rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
          />
        </div>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Phone size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (optional)"
              className="w-full border rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="flex-1 relative">
            <Mail size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (optional)"
              className="w-full border rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <div className="flex gap-2">
          {editingId && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-300 touch-manipulation"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 touch-manipulation disabled:opacity-50"
          >
            <Plus size={14} />
            {editingId ? "Save" : "Add Contact"}
          </button>
        </div>
      </form>

      {/* Contact list */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">
          No contacts yet. Add grandparents, coaches, etc. above.
        </p>
      ) : (
        <div className="space-y-2">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className="flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-3 py-2.5"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{contact.name}</p>
                {contact.email && <p className="text-xs text-gray-400">{contact.email}</p>}
                {contact.phone && <p className="text-xs text-gray-400">{contact.phone}</p>}
              </div>
              <button
                onClick={() => handleEdit(contact)}
                className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation px-2 py-1"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(contact.id)}
                className="text-gray-300 hover:text-red-400 touch-manipulation p-1"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExternalContactsTab;
